// 🧱🎛️ Build form v2: the PURPOSE registry (SPACES_REDESIGN_BRAINSTORM_2026_09_29
// §4.2 + §5.4; mockup https://claude.ai/artifact/KhGMVzvwkwm35Me94TDTsb, Shape A).
//
// "What's the main purpose of this space?" has six answers, and every answer
// has a few sub-picks. A sub-pick is a MAPPER ROW, not an engine: it names
// the closest of TODAY's six goal keys (the layout the viewer opens with) and
// a tool_flags preset built from switches the code already honors. Nothing
// here turns on a tool that does not exist yet (Room library, Papers search,
// dice, check-ins: awareness without ability is a trap), and every "Opens
// as…" line says only what those flags and that layout really do.
//
// The purpose itself is stored as its OWN key (data.purpose / purpose_kind,
// Nina's call 3, default taken 09-29), above the goal chips, so the
// three-table lockstep keeps working untouched:
//   · pages/CustomSpace.js    spaceGoalOptions (the old form's six chips)
//   · spacesConfigMapper.js   GOAL_PRESETS
//   · utils/spaceLayout.js    LAYOUT_DEFAULTS_BY_GOAL
// utils/spaceGoalParity.test.js checks every sub-pick against all three, and
// spacePurpose.test.js checks the keys against the backend's validator
// (justsomeguy-backend/src/ChatGame/spacePurpose.js), which drops anything
// else at create.
//
// Tool flags (the backend whitelist, 09-29): arcade · doodle · watch_party ·
// polls · media · terminal · crowd_inline. Only an explicit false turns a
// tool off, EXCEPT crowd_inline, which is opt-in TRUE and belongs to Live
// rooms only (utils/crowdInline.js). A sub-pick keeps every key of its goal's
// preset and only ever adds more `false`s.
//
// Pure: no React, no DOM. Player-facing copy: short, no em dashes, curly ’.

import { parseYouTubeUrl } from './lofiEmbed';
import { distributeModels } from './distributeModels';

// ── limits (the server's, gameValidation.js; the old form's caps) ──────────
export const NAME_MAX = 100;
export const QUESTION_MAX = 1000; // objective / scene_description cap
export const SEED_MAX = 500;      // a seat's character_description cap
export const TAGLINE_MAX = 80;
export const ART_MAX = 120;
export const SONG_MAX = 300;
// Nina 08-13: default cast 2, cap 3; one premium seat per space (07-12).
export const CAST_DEFAULT = 2;
export const CAST_MAX = 3;
export const PREMIUM_SEATS = 1;

// The backend's tool_flags whitelist (sanitizeToolFlags). A key outside it is
// dropped at create, so the registry may never use one.
export const TOOL_FLAG_KEYS = ['arcade', 'doodle', 'watch_party', 'polls', 'media', 'terminal', 'crowd_inline'];

// The 18 tags the old form offers (CustomSpace.js interestOptions), in its
// order. Parity-tested against that file's source: the Live room crowd's
// voice draw and the /explore categories are keyed on exactly these strings.
export const INTEREST_OPTIONS = ['Science/Technology', 'Music', 'Self Improvement', 'Relationships', 'Holiday/Culture', 'Fantasy/Fiction', 'Silly/Random', 'qt3.14s', 'Conversational Nook', 'Digital Content', 'Film', 'TV Shows', 'Gaming', 'ISSAVIBE', 'News/Politics', 'Language', 'Art', 'Storytelling/D&D'];

// Shared presets (each keeps its goal's GOAL_PRESETS keys, then subtracts).
const QUIET = { arcade: false, watch_party: false, polls: false, media: false }; // on top of story's {arcade, watch_party}
const TEA = { arcade: false, watch_party: false, doodle: false };                // = GOAL_PRESETS.tea
const QUIET_LINES = ['Opens quiet.', 'Side panels tucked away. No arcade, TV or polls.'];
const LIVE_LINES = ['Opens as a Live room.', 'The crowd talks in the main chat.'];

/**
 * The six purposes, in the form's order. Each kind:
 *   key      the sub-pick key sent as purpose_kind (/^[a-z][a-z_]{0,23}$/)
 *   label    the chip
 *   goal     one of today's six goal keys: the closest LAYOUT (space_goal)
 *   flags    the tool_flags preset
 *   tags     default Explore tags (INTEREST_OPTIONS), until the builder picks
 *   opens    [bold, rest]: the "Opens as…" line, true to goal + flags
 *   scene    the room's line when the question is left blank (it also seeds
 *            each local, byte-equal, so the designer swaps in a real bio)
 */
export const PURPOSES = [
  {
    key: 'learn', title: 'Productivity & learning', short: 'Productivity',
    blurb: 'Study buddies, tutors, focus rooms', icon: 'checklist', color: '#4DD0C0',
    kinds: [
      { key: 'study', label: 'Study group', goal: 'story', flags: QUIET, tags: ['Self Improvement'], opens: QUIET_LINES, scene: 'A study group that keeps each other on track.' },
      { key: 'cowork', label: 'Coworking', goal: 'story', flags: QUIET, tags: ['Self Improvement'], opens: QUIET_LINES, scene: 'A quiet coworking room. Heads down, check in now and then.' },
      { key: 'tutor', label: 'Tutor', goal: 'story', flags: QUIET, tags: ['Self Improvement'], opens: ['Opens quiet.', 'Side panels tucked away. Doodles on for drawing it out.'], scene: 'A patient tutor who explains things one step at a time.' },
    ],
    examples: [
      'Two of us are studying for the MCAT. Quiz us every night, keep us honest, and turn our notes into flashcards.',
      'Spanish practice for a trip in June. Talk to me only in Spanish and fix my grammar gently.',
      'A focus room for finishing my thesis. Ask what I got done every time I come back.',
    ],
    names: ['The Study Hall', 'Flashcard Fridays', 'Heads Down', 'Office Hours', 'Night Shift Notes'],
  },
  {
    key: 'research', title: 'Research & academia', short: 'Research',
    blurb: 'Lab groups, journal clubs, thesis help', icon: 'flask', color: '#6EA8FF',
    kinds: [
      { key: 'lab', label: 'Lab', goal: 'story', flags: QUIET, tags: ['Science/Technology'], opens: QUIET_LINES, scene: 'A lab group that shares new papers and its own data.' },
      { key: 'journal', label: 'Journal club', goal: 'story', flags: QUIET, tags: ['Science/Technology'], opens: QUIET_LINES, scene: 'A journal club that reads one paper at a time and argues it out.' },
      { key: 'deep_dive', label: 'Deep dive', goal: 'story', flags: QUIET, tags: ['Science/Technology'], opens: QUIET_LINES, scene: 'A deep dive into one hard question, from every side.' },
    ],
    examples: [
      'We’re a group of biologists who focus on the role of Piezo1 receptors. We share the latest journal articles and data from our own research.',
      'A journal club for early-career economists. One paper a week, argued from both sides.',
      'Help me pressure-test my dissertation chapter on urban heat islands.',
    ],
    names: ['The Mechanosensors', 'Channel Surfing', 'Pressure Point', 'Peer Review', 'Journal Club'],
  },
  {
    key: 'story', title: 'Storytelling & entertainment', short: 'Storytelling',
    blurb: 'D&D, live streams, podcasts, movies, games', icon: 'd20', color: '#E0525B',
    kinds: [
      { key: 'dnd', label: 'D&D campaign', goal: 'story', flags: { arcade: false, watch_party: false }, tags: ['Storytelling/D&D', 'Fantasy/Fiction'], opens: ['Opens as a story den.', 'Panels tucked away, the tale up front. No arcade or TV.'], scene: 'A D&D campaign table. The locals play the party.' },
      { key: 'roleplay', label: 'Roleplay', goal: 'story', flags: { arcade: false, watch_party: false }, tags: ['Storytelling/D&D'], opens: ['Opens as a story den.', 'Panels tucked away, the tale up front. No arcade or TV.'], scene: 'A roleplay where everyone stays in character.' },
      { key: 'live', label: 'Live stream or podcast', goal: 'live', flags: { crowd_inline: true }, tags: ['Digital Content'], opens: LIVE_LINES, scene: 'A live show with a loud, fast crowd.' },
      { key: 'watch', label: 'Watch party', goal: 'watch', flags: { arcade: false, doodle: false }, tags: ['Film', 'TV Shows'], opens: ['Opens for watching.', 'Space TV on. No arcade or doodles.'], scene: 'A watch party where the room reacts to every scene.' },
      { key: 'game', label: 'Game night', goal: 'game', flags: { watch_party: false }, tags: ['Gaming'], opens: ['Opens for games.', 'Arcade and doodles on. No TV.'], scene: 'A game night with the arcade open.' },
    ],
    examples: [
      'Four of us, level 1, just reached the Sunless Citadel. Run the dungeon, keep the dice honest, and give every goblin a name.',
      'A weekly movie club for bad 90s action films. We vote on Friday, watch on Sunday.',
      'A podcast about weird history. Two hosts, one skeptic, a loud crowd.',
    ],
    names: ['Roll for Initiative', 'Goblin Stairs', 'Bad Movie Club', 'The Late Show', 'Game Night'],
  },
  {
    key: 'social', title: 'Social & hangouts', short: 'Social',
    blurb: 'Random topics, cozy nooks, rainy music, arcade', icon: 'bubbles', color: '#9fd4ae',
    kinds: [
      { key: 'hangout', label: 'Hangout', goal: 'hangout', flags: {}, tags: ['ISSAVIBE', 'Conversational Nook'], opens: ['Opens as a hangout.', 'Every tool on: arcade, TV, polls, doodles.'], scene: 'A hangout where anything goes.' },
      { key: 'live', label: 'Live room', goal: 'live', flags: { crowd_inline: true }, tags: ['ISSAVIBE'], opens: LIVE_LINES, scene: 'A live room with a fast, chatty crowd.' },
      { key: 'nook', label: 'Cozy nook', goal: 'hangout', flags: { arcade: false, polls: false }, tags: ['Conversational Nook'], opens: ['Opens cozy.', 'No arcade or polls. Your song sets the mood.'], scene: 'A cozy nook for slow talk and good music.' },
    ],
    examples: [
      'A rainy day reading nook. Lofi, and everyone shares the best line from whatever they’re reading.',
      'A late-night hangout for people who can’t sleep. Weird questions only.',
      'Board game night energy. Trash talk encouraged.',
    ],
    names: ['Page & Puddle', 'Night Owls', 'The Back Booth', 'The Drizzle Library', 'Couch Club'],
  },
  {
    key: 'life', title: 'Personal & life', short: 'Personal',
    blurb: 'Life advice, motivation, AITA, relationships', icon: 'heart', color: '#F4A38C',
    kinds: [
      { key: 'advice', label: 'Advice', goal: 'tea', flags: TEA, tags: ['Self Improvement'], opens: ['Opens calm.', 'The crowd chat sits beside you. No arcade or TV.'], scene: 'A calm room for honest advice.' },
      { key: 'motivation', label: 'Motivation', goal: 'tea', flags: TEA, tags: ['Self Improvement'], opens: ['Opens calm.', 'The crowd chat sits beside you. No arcade or TV.'], scene: 'A room that cheers you on and keeps you going.' },
      { key: 'aita', label: 'AITA', goal: 'tea', flags: TEA, tags: ['Relationships'], opens: ['Opens as the jury.', 'The crowd chat weighs in beside you. No arcade or TV.'], scene: 'A jury that hears both sides and gives a verdict.' },
      { key: 'relationships', label: 'Relationships', goal: 'tea', flags: TEA, tags: ['Relationships'], opens: ['Opens calm.', 'The crowd chat sits beside you. No arcade or TV.'], scene: 'A warm room to talk relationships through.' },
    ],
    examples: [
      'Help me decide if I should move to Denver for a new job. Be honest, not nice.',
      'AITA for skipping my best friend’s wedding shower? The crowd decides.',
      'Keep me going on my first 5K. Ask about my runs and don’t let me quit.',
    ],
    names: ['Honest Friends Only', 'Porch Talk', 'The Jury', 'Hype Squad', 'Should I?'],
  },
  {
    key: 'art', title: 'Art & design', short: 'Art & design',
    blurb: 'Sketch nights, critiques, mood boards', icon: 'palette', color: '#E27AD6',
    kinds: [
      { key: 'drawing', label: 'Drawing', goal: 'hangout', flags: { arcade: false, watch_party: false }, tags: ['Art'], opens: ['Opens for drawing.', 'The doodle canvas is on. No arcade or TV.'], scene: 'A sketch club that draws together and shares.' },
      { key: 'music', label: 'Music', goal: 'watch', flags: { arcade: false, doodle: false }, tags: ['Music'], opens: ['Opens for music.', '/aux and Space TV on. No arcade.'], scene: 'A music room for sharing tracks and making things.' },
      { key: 'critique', label: 'Critique', goal: 'hangout', flags: { arcade: false, watch_party: false, polls: false }, tags: ['Art'], opens: ['Opens for sharing work.', 'Doodles on. No arcade, TV or polls.'], scene: 'A kind critique circle for work in progress.' },
    ],
    examples: [
      'We draw one prompt a day and critique each other kindly. This week: hands.',
      'Mood boards for a tiny bakery’s new look. Warm, a little retro.',
      'A room for sharing demos. Honest notes, no gatekeeping.',
    ],
    names: ['Daily Sketch Club', 'Kind Crit', 'Hands Week', 'Demo Tape', 'Mood Board'],
  },
];

export const PURPOSE_KEYS = PURPOSES.map((p) => p.key);

/** The purpose record for a key, or null. */
export function purposeFor(key) {
  return PURPOSES.find((p) => p.key === key) || null;
}

/** The sub-pick for (purpose, kind); an unknown kind falls back to the first. */
export function kindFor(purposeKey, kindKey) {
  const p = purposeFor(purposeKey);
  if (!p) return null;
  return p.kinds.find((k) => k.key === kindKey) || p.kinds[0];
}

/** The tool_flags a sub-pick stamps (a fresh copy, safe to mutate). */
export function flagsFor(purposeKey, kindKey) {
  const k = kindFor(purposeKey, kindKey);
  return k ? { ...k.flags } : {};
}

/** Space TV's default for a sub-pick: on unless its preset turns watch parties off. */
export function tvDefaultFor(purposeKey, kindKey) {
  const k = kindFor(purposeKey, kindKey);
  return !k || k.flags.watch_party !== false;
}

// ── copy (player-facing: short, no em dashes, curly ’) ─────────────────────
export const COPY = {
  purposeQ: 'What’s the main purpose of this space?',
  questionQ: 'Is there a specific question or idea you want it centered around?',
  questionSub: 'The AIs keep it in mind. It shows under the name on Explore until you set a tagline.',
  nameHelp: 'Shows on Explore and at the top of the room.',
  songQ: 'Your room’s song',
  songSub: 'Pick the song that plays when people walk in. Very 2006.',
  songPlays: 'Plays when people walk in',
  songPlaylist: 'Playlists turn off /aux in this room.',
  songPlaylistMore: 'Paste one song to keep /aux on.',
  songNotYouTube: 'Only YouTube links play here.',
  songShortsLive: 'Shorts and live links don’t play here. Paste a regular video link.',
  songNotVideo: 'Paste a link to one YouTube video or playlist.',
  listed: 'Show on Explore',
  listedOn: 'Anyone can find it on Explore.',
  listedOff: 'Unlisted: not on Explore. Anyone with the link can open it. Our Terms still apply.',
  // the ?agentId= door: an unlisted room built FOR an imported AI is the one
  // truly private room (AgentImportPage's shipped wording, Terms §5)
  listedOffAgent: 'Private: just you and {name}. It only opens in this browser.',
  chipIn: 'Let people chip in',
  chipInSub: 'Visitors can tip to keep the battery charged.',
  chipInTip: 'Adds a Chip in button to this space’s battery. Tips only pay the AI bill.',
  ready: 'Ready in about 15 seconds',
  build: 'Build it',
  building: 'Building…',
  errPurpose: 'Pick what it’s for.',
  errName: 'Give it a name.',
  errSong: 'Fix the song link or clear it.',
  errSlow: 'Slow down a sec, then try again.',
  errFailed: 'That didn’t work. Try again in a moment.',
  errFlagged: 'Some of that got flagged. Change it and try again.',
  placeholderNote: 'The real banner and faces are painted when you build.',
  // 🧰 10-01 (Nina): the classic form's quiet door, under Premium and details
  moreOptions: 'More options',
  moreOptionsSub: 'Personality dials and a creator name, in the classic form.',
};

// ── the song link ───────────────────────────────────────────────────────────
const YT_HOST_RE = /^(?:[a-z0-9-]+\.)?(?:youtube\.com|youtu\.be|youtube-nocookie\.com)$/i;

/**
 * The music link as the player will see it. lofiEmbed.parseYouTubeUrl is the
 * truth for what plays; this adds the host check (it reads any `?v=` link)
 * and names the two shapes that fall back to generic lofi (Shorts, /live).
 * → { empty } | { ok, type: 'video'|'playlist', id, startSeconds, url } | { bad: reasonCopy }
 */
export function parseSongLink(raw) {
  const url = String(raw || '').trim();
  if (!url) return { empty: true };
  let host = '';
  try { host = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname; } catch (e) { host = ''; }
  if (!YT_HOST_RE.test(host)) return { bad: COPY.songNotYouTube };
  if (/youtube\.com\/(?:shorts|live)\//i.test(url)) return { bad: COPY.songShortsLive };
  const parsed = parseYouTubeUrl(url);
  if (!parsed) return { bad: COPY.songNotVideo };
  return { ok: true, type: parsed.type, id: parsed.id, startSeconds: parsed.startSeconds || 0, url };
}

/** 90 → '1:30', 3725 → '1:02:05'. */
export function clockOf(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

// ── the question → objective + the seats' seed ─────────────────────────────
/**
 * The question goes VERBATIM into the room's objective (1000 cap). Every
 * local is seeded with ONE ≤500 string that is ALSO the scene_description,
 * byte for byte: the backend's isCopiedSeed only swaps a seed for a real bio
 * when it equals the scene or the objective (NONCOMP_CRISPING §3.1). A long
 * question is cut at its last full sentence inside 500 chars (。！？ count
 * too: CJK has no space after them); with no sentence end there, at the last
 * word, plus "…".
 */
export function seedFromQuestion(question, max = SEED_MAX) {
  const q = String(question || '').trim();
  if (q.length <= max) return q;
  let cut = -1;
  // the lookahead reads the WHOLE question: "2." at the 500 mark of "2.5" is no sentence end
  const re = /[.!?…](?=\s|$)|[。！？]/g;
  let m;
  while ((m = re.exec(q)) !== null && m.index < max) cut = m.index + 1;
  if (cut >= Math.floor(max * 0.4)) return q.slice(0, cut).trim();
  const room = clampChars(q, max - 1);
  const sp = room.lastIndexOf(' ');
  return `${(sp > max * 0.4 ? room.slice(0, sp) : room).trim()}…`;
}

/**
 * The first `max` UTF-16 units of `s`, never ending on half of an emoji: a
 * lone surrogate is refused by Postgres jsonb, so a create carrying one 500s.
 */
export function clampChars(s, max) {
  const out = String(s || '').slice(0, max);
  return /[\uD800-\uDBFF]$/.test(out) ? out.slice(0, -1) : out;
}

// ── "Suggest one": a name from the question or the purpose, zero LLM ──────
const NAME_SUFFIX = {
  study: ['Study Hall', 'Study Group'], cowork: ['Cowork', 'Focus Room'], tutor: ['Tutoring', 'Study Hall'],
  lab: ['Lab', 'Research Group'], journal: ['Journal Club', 'Reading Group'], deep_dive: ['Deep Dive', 'Rabbit Hole'],
  dnd: ['Campaign', 'Table'], roleplay: ['Roleplay', 'Stories'], live: ['Live', 'Show'], watch: ['Watch Party', 'Movie Club'], game: ['Game Night', 'Arcade'],
  hangout: ['Hangout', 'Lounge'], nook: ['Nook', 'Corner'],
  advice: ['Advice', 'Porch'], motivation: ['Hype Squad', 'Pep Talk'], aita: ['Jury', 'Verdict'], relationships: ['Porch Talk', 'Heart to Heart'],
  drawing: ['Sketch Club', 'Studio'], music: ['Music Room', 'Listening Party'], critique: ['Crit Night', 'Studio'],
};
const NAME_STOP = new Set(['I', 'I’m', "I'm", 'We', 'We’re', "We're", 'AI', 'AIs', 'AITA', 'OK', 'The', 'A', 'An', 'My', 'Our', 'You',
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']);

/**
 * The first distinctive phrase in the question: up to three touching words
 * that start with a capital or carry a digit (Piezo1, MCAT, Sunless Citadel,
 * Denver), skipping sentence starts and the stop list.
 */
export function keyPhrase(question) {
  const words = String(question || '').replace(/\s+/g, ' ').trim().split(' ');
  let run = [];
  const out = () => run.join(' ');
  for (let i = 0; i < words.length; i += 1) {
    const raw = words[i];
    const w = raw.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').replace(/[’']s$/u, '');
    const sentenceStart = i === 0 || /[.!?…:]$/.test(words[i - 1]);
    const distinctive = w.length > 1 && !NAME_STOP.has(w) && (/\d/.test(w) ? /\p{L}/u.test(w) : /^\p{Lu}/u.test(w));
    if (distinctive && !(sentenceStart && !/\d|^\p{Lu}{2,}/u.test(w))) {
      run.push(w);
      if (run.length === 3 || /[,.!?;:…)]$/.test(raw)) return out();
    } else if (run.length) {
      return out();
    }
  }
  return out();
}

/**
 * Suggestion number `n` for this room (a press = n + 1). From the question's
 * key phrase first ("Piezo1 Lab"), then the purpose's own bank. Deterministic.
 */
export function suggestName({ question = '', purpose = null, kind = null, n = 0 } = {}) {
  const p = purposeFor(purpose);
  const k = kindFor(purpose, kind);
  const key = keyPhrase(question);
  const cands = [];
  if (key) {
    (NAME_SUFFIX[k && k.key] || ['Club']).forEach((sfx) => cands.push(`${key} ${sfx}`));
    if (key.includes(' ')) cands.push(key);
  }
  (p ? p.names : ['The Hangout', 'Night Owls', 'The Back Booth']).forEach((nm) => cands.push(nm));
  const uniq = [...new Set(cands.map((c) => c.slice(0, NAME_MAX).trim()).filter(Boolean))];
  const i = ((Number(n) || 0) % uniq.length + uniq.length) % uniq.length;
  return uniq[i];
}

// ── the cast: battery drain, never dollars ─────────────────────────────────
// Output $/M from OpenRouter's public list, read 09-29 (ground-truth
// build-form.md fact 31). Light ≤ $2 · Standard ≤ $5 · Heavy above, and every
// premium (or thinking) model reads Heavy. An unknown id falls back on the
// roster's premium flag.
const OUT_PRICE = {
  'google/gemini-2.5-pro-preview': 10, 'google/gemini-2.5-pro': 10, 'openai/gpt-5.1': 10,
  'qwen/qwen3-max-thinking': 3.9, 'anthropic/claude-haiku-4.5': 5, 'google/gemini-3-flash-preview': 3,
  'moonshotai/kimi-k2-0905': 2.5, 'openai/gpt-5-mini': 2, 'deepseek/deepseek-v3.2': 0.42,
  'meta-llama/llama-4-scout': 0.3, 'qwen/qwen3-30b-a3b-instruct-2507': 0.193, 'qwen/qwen3.7-flash': 0.13,
};
export const DRAIN_LABEL = { light: 'Light', standard: 'Standard', heavy: 'Heavy' };

/** 'light' | 'standard' | 'heavy' for a roster model ({ id, premium }). */
export function drainFor(model) {
  if (!model) return 'standard';
  if (model.premium) return 'heavy';
  const price = OUT_PRICE[model.id];
  if (price == null) return 'standard';
  if (price <= 2) return 'light';
  if (price <= 5) return 'standard';
  return 'heavy';
}

const PROVIDERS = { google: 'Google', openai: 'OpenAI', anthropic: 'Anthropic', deepseek: 'DeepSeek', 'meta-llama': 'Meta', qwen: 'Alibaba', moonshotai: 'Moonshot' };
/**
 * The cast as the mapper wants it ({ [rosterName]: seats }), from the drawer's
 * seat picks: a model name, or 'surprise' for a draw from the default pool.
 * Surprise seats never repeat a model another seat already has (the 08-28
 * no-duplicate draw), unless the pool runs out.
 */
export function castCounts(seats, names = [], pool = []) {
  const counts = Object.fromEntries((names || []).map((n) => [n, 0]));
  const picked = (seats || []).filter((s) => s && s !== 'surprise' && (names || []).includes(s));
  picked.forEach((n) => { counts[n] += 1; });
  const surprise = (seats || []).length - picked.length;
  if (surprise > 0) {
    const free = (pool || []).filter((n) => !picked.includes(n));
    const drawn = distributeModels(surprise, names, free.length ? free : pool);
    Object.entries(drawn).forEach(([n, c]) => { counts[n] = (counts[n] || 0) + c; });
  }
  return counts;
}

/** 'ChatGPT (GPT-5.1)' → 'GPT-5.1' (the roster's names are the old form's). */
export const modelLabel = (name) => String(name || '').replace(/^ChatGPT \((.+)\)$/, '$1');
/** 'openai/gpt-5.1' → 'OpenAI'. */
export const providerOf = (id) => PROVIDERS[String(id || '').split('/')[0]] || '';
